import { jsx as _jsx } from "react/jsx-runtime";
import { findingViewSectionType_Graph, findingViewType_Sections } from '@app/advisor-ui-shared';
import { AwaitModules, ComponentSync, ModuleFE_MouseInteractivity, mouseInteractivity_ToolTip } from '@nu-art/thunderstorm-frontend';
import { Component_Vitals } from '@app/advisor-ui-frontend/ui/_components/Component_Vitals/Component_Vitals';
import { AdvisorConfigKeyFE_HomePanel, ModuleFE_Advisor_FindingView, ModuleFE_Advisor_MeasurementUnit, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_AdvisorSession, ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { filterDuplicates, Second, sortArray } from '@nu-art/ts-common';
import { valueType_Range, valueType_StandardRange } from '@app/hcs-shared';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
import { ModuleFE_PanelFindings } from '../../../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
import { Component_AdvisorSL_CatchUpVitals } from '../../../loaders/skeleton/catch-up/vitals/Component_AdvisorSL_CatchUpVitals.js';
import { ModuleFE_AdvisorMini } from '../../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Findings } from '@app/advisor-v5-shared';
import { FindingType_Enumerated } from '@app/advisor-shared';
const modulesToAwait = [
    //Static Modules
    ModuleFE_Advisor_FindingView,
    ModuleFE_Advisor_Variable,
    ModuleFE_Advisor_Value,
    ModuleFE_Advisor_MeasurementUnit,
    //Runtime Modules
    ModuleFE_RuntimeFinding,
];
class Component_Vitals_Advisor extends Component_Vitals {
    modules = {
        variable: ModuleFE_Advisor_Variable,
        value: ModuleFE_Advisor_Value,
        measurementUnit: ModuleFE_Advisor_MeasurementUnit,
    };
}
class Component extends ComponentSync {
    // ######################## Life Cycle ########################
    __onRuntimeFindingsUpdated = (...params) => {
        if (!this.state.vitalsFV)
            return;
        this.setState({ vitalResults: this.calculateResults(this.state.vitalsFV) });
    };
    deriveStateFromProps(nextProps, state) {
        state.vitalsFV ??= this.resolveFV();
        state.vitalResults = this.calculateResults(state.vitalsFV);
        return state;
    }
    // ######################## Logic ########################
    resolveFV() {
        const config = AdvisorConfigKeyFE_HomePanel.get();
        return ModuleFE_Advisor_FindingView.cache.unique(config?.vitalsFVId);
    }
    calculateResults(findingView) {
        const results = {};
        if (findingView?.type !== findingViewType_Sections)
            return results;
        const variableIds = new Set();
        const abnormalityIdMap = {};
        //Collect ids
        findingView.sections.forEach(section => {
            if (section.type !== findingViewSectionType_Graph)
                return;
            //Find abnormality
            const abnormalityValueIds = this.calculateSection_AbnormalityId(section.data.alert);
            section.data.variableIds.forEach(id => {
                variableIds.add(id);
                if (abnormalityValueIds?.length)
                    abnormalityIdMap[id] = abnormalityValueIds;
            });
        });
        variableIds.forEach((id) => {
            //Translate into results
            results[id] = {
                results: this.calculateResults_Findings(id),
                normalRange: this.calculateResults_NormalRange(id),
                measurementUnitId: this.calculateResults_MeasurementUnitId(id),
                abnormalityValueIds: abnormalityIdMap[id],
            };
        });
        return results;
    }
    calculateResults_Findings(variableId) {
        const encounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        const results = [];
        ModuleFE_RuntimeFinding.cache.forEach(finding => {
            if (finding.variableId !== variableId || finding.type !== 'numeric')
                return;
            results.push({
                value: finding.value,
                timestamp: finding.issuedTimestamp ?? finding.__created,
                currentVisit: finding.encounterId === encounterId,
            });
        });
        return results;
    }
    ;
    calculateResults_NormalRange(variableId) {
        return [0, 0];
    }
    ;
    calculateResults_MeasurementUnitId(variableId) {
        const variable = ModuleFE_Advisor_Variable.cache.unique(variableId);
        const value = ModuleFE_Advisor_Value.cache.unique(variable?.valueId);
        switch (value?.type) {
            case valueType_Range:
            case valueType_StandardRange: {
                return value.data.unitsId;
            }
        }
    }
    calculateSection_AbnormalityId(alertData) {
        if (!alertData)
            return;
        let findings = ModuleFE_RuntimeFinding.cache.filter(finding => finding.variableId === alertData.variableId && finding.type === FindingType_Enumerated);
        if (!findings.length)
            return;
        //Sort findings by timestamp
        sortArray(findings, f => f.issuedTimestamp, true);
        //Filter out duplicates by valueId
        findings = filterDuplicates(findings, f => f.valueIds[0]);
        //Filter out findings that are older than 5 seconds of the latest alert finding
        const latestTS = findings[0].issuedTimestamp;
        const toRet = findings.filter(f => (latestTS - f.issuedTimestamp) <= 5 * Second);
        //Weird exit clause - if one of the findings is the normal value, don't return anything.
        //Content wise in KM we should make organize the data such that "normal" value is never generated along with any "abnormal" value.
        if (toRet.find(f => f.valueIds[0] === alertData.normalValueId))
            return undefined;
        return toRet.map(f => f.valueIds[0]);
    }
    // ######################## Render ########################
    render() {
        const fv = this.state.vitalsFV;
        if (!fv)
            return;
        return _jsx(Component_Vitals_Advisor, { findingView: fv, results: this.state.vitalResults, onClick: () => {
                ModuleFE_MouseInteractivity.hide(mouseInteractivity_ToolTip, true);
                ModuleFE_PanelFindings.setFindingView(fv);
                ModuleFE_AdvisorMini.openPanel(PanelKey_Findings);
            }, onCellTooltipOpen: (section) => {
                ModuleFE_AdvisorAnalytics.trigger.view.view_vitals({ sectionLabel: section.label });
            } });
    }
}
export const Component_CatchUp_Vitals = (props) => {
    const advisorConfig = ModuleFE_AdvisorSession.getAdvisorConfig();
    if (!advisorConfig?.catchUp?.vitals)
        return;
    return _jsx(AwaitModules, { modules: modulesToAwait, Component: () => _jsx(Component, { ...props }), customLoader: Component_AdvisorSL_CatchUpVitals });
};
//# sourceMappingURL=Component_CatchUp_Vitals.js.map